import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, GripVertical, Loader2 } from "lucide-react";

export interface ColunaOrdenavel {
  tipo: string;
  nome: string;
  /** Texto de apoio (ex.: "excluído", "não está nesta folha"). */
  apoio?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Na ordem atual. */
  colunas: ColunaOrdenavel[];
  /** Ordem do catálogo, para "Restaurar padrão". */
  padrao: string[];
  onSalvar: (ordem: string[]) => Promise<void>;
}

/** Ordena as colunas de benefícios: arrastando ou pelas setas. */
export const OrdenarColunasModal = ({ open, onClose, colunas, padrao, onSalvar }: Props) => {
  const [lista, setLista] = useState<ColunaOrdenavel[]>([]);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) setLista(colunas);
    // Só ao abrir: depois vale o que o usuário está arrumando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mover = (de: number, para: number) => {
    if (para < 0 || para >= lista.length || de === para) return;
    setLista((atual) => {
      const nova = [...atual];
      const [item] = nova.splice(de, 1);
      nova.splice(para, 0, item);
      return nova;
    });
  };

  const restaurar = () => {
    const pos = new Map(padrao.map((t, i) => [t, i]));
    setLista((atual) => [...atual].sort((a, b) => (pos.get(a.tipo) ?? Infinity) - (pos.get(b.tipo) ?? Infinity)));
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await onSalvar(lista.map((c) => c.tipo));
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[440px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ordem das colunas</DialogTitle>
          <DialogDescription>
            Arraste ou use as setas. A ordem fica salva só para você e vale em todas as folhas de benefícios.
            Colaborador e Total continuam nas pontas; a planilha da Flash não muda.
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-1.5">
          {lista.map((c, i) => (
            <li
              key={c.tipo}
              draggable
              onDragStart={() => setArrastando(i)}
              onDragOver={(e) => {
                e.preventDefault();
                if (arrastando === null || arrastando === i) return;
                mover(arrastando, i);
                setArrastando(i);
              }}
              onDragEnd={() => setArrastando(null)}
              className={cn(
                "flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-sm",
                arrastando === i && "border-primary bg-primary/5",
              )}
            >
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" />
              <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate font-medium">
                {c.nome}
                {c.apoio && <span className="ml-1.5 text-xs font-normal text-muted-foreground">{c.apoio}</span>}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => mover(i, i - 1)}
                disabled={i === 0}
                aria-label={`Subir ${c.nome}`}
              >
                <ArrowUp className="!size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => mover(i, i + 1)}
                disabled={i === lista.length - 1}
                aria-label={`Descer ${c.nome}`}
              >
                <ArrowDown className="!size-3.5" />
              </Button>
            </li>
          ))}
        </ul>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={restaurar} disabled={salvando}>
            Restaurar padrão
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={() => void salvar()} disabled={salvando}>
              {salvando && <Loader2 className="animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
